import {Component, OnInit, HostListener} from '@angular/core';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {AuthenticationService} from '../../services/auth/authentication.service';
import {ApiService} from '../../services/api/api.service';
import {FormatService} from '../../services/format/format.service';

declare const $: any;

@Component({
  selector: 'app-subscription-plan',
  templateUrl: './subscription-plan.component.html',
  styleUrls: ['./subscription-plan.component.css']
})
export class SubscriptionPlanComponent implements OnInit {
  isForm: any;
  isData: any = [];
  isTotalAll: number = 0;
  isKeyword: string = '';
  isPlatform: string = '';
  isStatus: string = '';

  isPage: number = 1;
  isPageSize: number = 10;
  isPages: any = [];
  isTableLoading: boolean = false;

  isLoading = false;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private authService: AuthenticationService,
    private apiService: ApiService,
    private formatService: FormatService,
  ) { }

  ngOnInit(): void {
    this.isPageSize = this.apiService.LIMIT || 10;
    this.resetForm();
    this.getData();
  }

  async resetForm() {
    this.isForm = this.fb.group({
      id: new FormControl(''),
      platform: new FormControl('android', [Validators.required]),
      product_id: new FormControl('', [Validators.required]),
      title: new FormControl('', [Validators.required]),
      months: new FormControl('', [Validators.required, Validators.min(1)]),
      sort: new FormControl(0),
      is_best: new FormControl(false),
      status: new FormControl(1, [Validators.required]),
      // Only meaningful for platform = 'web' — android/ios prices still come
      // from Play Console / App Store Connect, not this field.
      price: new FormControl(null, [Validators.min(0)]),
    })
  }

  private searchTimer: any;
  private lastQuery: string = '';

  onKeywordInput(value: string) {
    this.isKeyword = value;
    clearTimeout(this.searchTimer);
    // debounce: wait 450ms after the last keystroke before hitting the API
    this.searchTimer = setTimeout(() => this.applyFilter(), 450);
  }

  async applyFilter() {
    clearTimeout(this.searchTimer);
    const query = `${this.isKeyword}|${this.isPlatform}|${this.isStatus}`;
    if (query === this.lastQuery) { return; } // query unchanged, skip the request
    this.lastQuery = query;
    this.isPage = 1;
    await this.getData();
  }

  async getData() {
    let searchData = {"keyword": this.isKeyword, "platform": this.isPlatform, "status": this.isStatus};
    const offset = (this.isPage - 1) * this.isPageSize;
    this.isTableLoading = true;
    try {
      this.apiService.methodGetSilent(`subscription-plan/${offset}/${encodeURIComponent(JSON.stringify(searchData))}`, this.authService.getToken()).subscribe((response) => {
        this.isTableLoading = false;
        if (response && response.status == 200) {
          this.isData = [];
          (response.total ? response.data.forEach((element: any) => {
            element.platformVw = element.platform === 'android' ? 'Play Store'
              : (element.platform === 'ios' ? 'App Store'
                : (element.platform === 'web' ? 'Web' : (element.platform || '-')));
            this.isData.push(element);
          }) : false);
          this.isTotalAll = response.total || 0;
          this.buildPager();
        }
      }, () => { this.isTableLoading = false; })
    } catch (error) {
      this.isTableLoading = false;
      console.log(error);
    }
  }

  totalPages() {
    return Math.max(1, Math.ceil(this.isTotalAll / this.isPageSize));
  }
  buildPager() {
    const total = this.totalPages();
    const pages: any[] = [];
    for (let p = 1; p <= total; p++) {
      if (p == 1 || p == total || Math.abs(p - this.isPage) <= 1) {
        pages.push(p);
      } else if (pages[pages.length - 1] != '...') {
        pages.push('...');
      }
    }
    this.isPages = pages;
  }
  async goToPage(p: any) {
    const total = this.totalPages();
    if (p < 1 || p > total || p == this.isPage) { return; }
    this.isPage = p;
    await this.getData();
  }
  rowNo(i: number) {
    return (this.isPage - 1) * this.isPageSize + i + 1;
  }
  summaryFrom() {
    return this.isTotalAll ? (this.isPage - 1) * this.isPageSize + 1 : 0;
  }
  summaryTo() {
    return (this.isPage - 1) * this.isPageSize + this.isData.length;
  }
  formatNumber(value: any) {
    return this.formatService.dotFormat(value || 0);
  }
  formatCurrency(value: any) {
    return this.formatService.currency(value || 0);
  }

  // Live thousand-separator display for the price field — kept separate
  // from formatNumber() because that defaults null to "0", which would
  // show a phantom "0" in an otherwise-empty (not-yet-filled) field.
  formatPriceInput(value: any) {
    return value === null || value === undefined || value === ''
      ? ''
      : this.formatService.dotFormat(value);
  }

  // The input shows "99.000" but the FormControl must hold a plain number
  // for the API payload and Validators.min — strip the dots back out here.
  onPriceInput(event: any) {
    const digits = (event.target.value || '').replace(/\D/g, '');
    const numeric = digits === '' ? null : parseInt(digits, 10);
    this.isForm.get('price').setValue(numeric);
    event.target.value = this.formatPriceInput(numeric);
  }

  // wrap keyword matches in <mark> for the list; input is HTML-escaped first
  highlight(text: any) {
    const esc = String(text == null ? '' : text)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const keyword = this.isKeyword.trim();
    if (!keyword) { return esc; }
    const escapedKeyword = keyword
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // neutralize regex metacharacters
    return esc.replace(new RegExp(`(${escapedKeyword})`, 'gi'), '<mark class="vl-hl">$1</mark>');
  }

  // ===== delete confirmation (custom dialog, not SweetAlert) =====
  isConfirm: any = null; // {v}
  isDeleting: boolean = false;

  onDelete(v: any) {
    this.isConfirm = {v: v};
    document.body.style.overflow = 'hidden'; // lock the page scroll behind the dialog
    setTimeout(() => { // focus the Cancel button so the keyboard lands inside the dialog
      const btn: any = document.querySelector('.hds-dialog .vl-btn-ghost');
      if (btn) { btn.focus(); }
    });
  }

  closeConfirm() {
    if (this.isDeleting) { return; } // never close while a deletion is in flight
    this.isConfirm = null;
    document.body.style.overflow = '';
  }

  @HostListener('document:keydown.escape')
  onEscapeConfirm() {
    if (this.isConfirm) { this.closeConfirm(); }
  }

  // reload without discarding filter/page; step back one page if this page becomes empty
  async refreshAfterMutation() {
    await this.getData();
    if (this.isData.length === 0 && this.isPage > 1) {
      this.isPage = this.isPage - 1;
      await this.getData();
    }
  }

  async confirmDelete() {
    const c = this.isConfirm;
    if (!c || this.isDeleting) { return; }
    this.isDeleting = true;
    this.apiService.methodDeleteSilent(`subscription-plan/${c.v.id}`, this.authService.getToken()).subscribe(async (response) => {
      this.isDeleting = false;
      if (response && response.status == 200) {
        this.closeConfirm();
        await this.toastr.success(`${c.v.title}`, `Paket dihapus`);
        this.refreshAfterMutation();
      } else {
        // failure: keep the dialog open so the user can retry
        await this.toastr.error((response && response.message) || `Coba lagi beberapa saat.`, `Gagal menghapus`);
      }
    }, async () => {
      this.isDeleting = false;
      await this.toastr.error(`Periksa koneksi lalu coba lagi.`, `Gagal menghapus`);
    });
  }

  // ===== form dialog =====
  async onShowAdd() {
    await this.resetForm();
    $('#formData').modal({backdrop: 'static', keyboard: false})
  }

  // show inline errors once a field is touched / after a submit attempt
  isInvalid(control: string) {
    const c = this.isForm.get(control);
    return c && c.invalid && (c.touched || c.dirty);
  }

  async onEdit(v: any) {
    try {
      await this.resetForm();
      await this.isForm.patchValue({
        id: v.id,
        platform: v.platform,
        product_id: v.product_id,
        title: v.title,
        months: v.months,
        sort: v.sort,
        is_best: !!v.is_best,
        status: v.status,
        price: v.price,
      });
      // keyboard: false — Escape must never dismiss the dialog mid-save
      await $('#formData').modal({backdrop: 'static', keyboard: false, show: true});
    } catch (error) {
      console.log(error);
    }
  }

  async onSubmit() {
    try {
      if (this.isForm.invalid) {
        this.isForm.markAllAsTouched();
        return;
      }
      if (this.isLoading) { return; }
      this.isLoading = true;
      const payload = this.isForm.getRawValue();
      const isEdit = !!payload.id;
      const request = isEdit
        ? this.apiService.methodPutSilent(`subscription-plan/${payload.id}`, payload, this.authService.getToken())
        : this.apiService.methodPostSilent(`subscription-plan`, payload, this.authService.getToken());
      request.subscribe(async (response) => {
        this.isLoading = false;
        if (response && response.status == 200) {
          // close the modal only on success — on failure the user's input stays intact
          $('#formData').modal('hide');
          await this.toastr.success(`${payload.title}`, isEdit ? `Paket diupdate` : `Paket disimpan`);
          if (isEdit) {
            await this.refreshAfterMutation(); // keep the user's page & filter
          } else {
            this.isPage = 1;
            await this.getData(); // new item sits on page 1 (newest first)
          }
        } else {
          await this.toastr.error((response && response.message) || `Periksa isian lalu coba lagi.`, `Gagal menyimpan`);
        }
      }, async () => {
        this.isLoading = false;
        await this.toastr.error(`Periksa koneksi lalu coba lagi.`, `Gagal menyimpan`);
      });
    } catch (error) {
      this.isLoading = false;
      await this.toastr.error(`Periksa isian lalu coba lagi.`, `Gagal menyimpan`);
    }
  }
}
